import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "@tanstack/react-router";
import { Responsive, WidthProvider, type Layouts } from "react-grid-layout";
import { ArrowLeft, ArrowUpCircle, BadgeCheck, Download, EyeOff, Heart, Sparkles, Trash2 } from "lucide-react";
import { CATEGORY_LABELS, GRID_COLS, isStaff, type InstalledCopy, type ListingDetail as Detail, type Widget } from "@cd/core";
import { capabilities } from "../../lib/access";
import { api } from "../../lib/api";
import { asSelection, selectionLabel } from "../../lib/time";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Badge, Skeleton } from "../../components/ui/primitives";
import { WidgetCard } from "../dashboard/WidgetCard";
import { InstallDialog } from "./InstallDialog";
import { UpdateDialog } from "./UpdateDialog";
import { KindBadge, plural, VerifiedBadge } from "./ListingCard";

const Grid = WidthProvider(Responsive);
const BREAKPOINTS = { lg: 1100, md: 700, sm: 0 };
const noop = () => {};

export function ListingDetailPage({ id }: { id: string }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const listing = useQuery({ queryKey: ["listing", id], queryFn: () => api.listing(id) });
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: Infinity });
  const [installOpen, setInstallOpen] = useState(false);
  const [updating, setUpdating] = useState<InstalledCopy>();

  const refresh = (l: Partial<Detail>) => {
    qc.setQueryData<Detail>(["listing", id], (cur) => cur && { ...cur, ...l });
    qc.invalidateQueries({ queryKey: ["marketplace"] });
    qc.invalidateQueries({ queryKey: ["marketplace-mine"] });
  };
  const like = useMutation({ mutationFn: (liked: boolean) => api.like(id, liked), onSuccess: refresh });
  const moderate = useMutation({ mutationFn: (c: Parameters<typeof api.moderate>[1]) => api.moderate(id, c), onSuccess: refresh });
  const unpublish = useMutation({
    mutationFn: () => api.unpublish(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["marketplace"] });
      qc.invalidateQueries({ queryKey: ["marketplace-mine"] });
      void navigate({ to: "/marketplace" });
    },
  });
  const [confirmUnpublish, setConfirmUnpublish] = useState(false);

  if (listing.isLoading) return <div className="mx-auto max-w-[1400px] px-6 pt-6"><Skeleton className="h-8 w-80" /><Skeleton className="mt-6 h-72" /></div>;
  if (listing.error || !listing.data) return <p className="p-8 text-bad">{listing.error?.message ?? "Listing not found"}</p>;
  const l = listing.data;
  // While viewing as someone: no liking, moderating or unpublishing for them; installs only as a super user, editing.
  const can = capabilities(me.data);
  const staff = can.own && (me.data ? isStaff(me.data) : false);
  const outdated = l.copies.filter((c) => c.version < l.version);
  const copyTitle = (c: InstalledCopy) => (l.kind === "dashboard" ? c.dashboardTitle : `${l.title} on ${c.dashboardTitle}`);
  const error = like.error ?? moderate.error ?? unpublish.error;

  return (
    <div className="mx-auto max-w-[1400px] px-6 pb-16">
      <header className="-mx-6 mb-6 border-b border-border px-6 py-4">
        <Link to="/marketplace" className="inline-flex items-center gap-1 text-xs text-muted hover:text-foreground"><ArrowLeft className="size-3" /> Marketplace</Link>
        <div className="mt-2 flex flex-wrap items-start gap-4">
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-1.5">
              <KindBadge kind={l.kind} />
              <Badge>{CATEGORY_LABELS[l.category]}</Badge>
              {l.verified && <VerifiedBadge />}
              {l.featured && <Badge tone="accent"><Sparkles className="size-3" /> Featured</Badge>}
              {l.status === "unlisted" && <Badge tone="warn"><EyeOff className="size-3" /> Unlisted</Badge>}
            </div>
            <h1 className="mt-2 text-xl font-semibold tracking-tight">{l.title}</h1>
            <p className="mt-0.5 text-sm text-muted">
              by {l.publisher} · v{l.version} · {plural(l.installers, "installer")} · {plural(l.likes, "like")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {can.own && (
              <Button onClick={() => like.mutate(!l.likedByMe)} disabled={like.isPending} aria-pressed={l.likedByMe}>
                <Heart className={cn("size-3.5", l.likedByMe && "fill-current text-accent")} /> {l.likedByMe ? "Liked" : "Like"}
              </Button>
            )}
            {l.status === "listed" && can.install && (
              <Button variant={l.copies.length ? "secondary" : "primary"} onClick={() => setInstallOpen(true)}>
                <Download className="size-3.5" /> {l.copies.length ? "Install another copy" : "Install"}
              </Button>
            )}
          </div>
        </div>
        {l.status === "listed" && outdated.length > 0 ? (
          <div className="mt-3 rounded-lg bg-warn-bg px-3 py-2 text-xs text-warn-fg">
            <p className="flex items-start gap-1.5">
              <ArrowUpCircle className="mt-px size-3.5 shrink-0" />
              <span>
                <span className="font-medium">v{l.version} is out.</span> Updating a copy keeps its link, name, filters and sharing; you'll see what it
                replaces before anything changes.
              </span>
            </p>
            <ul className="mt-1.5 space-y-1 pl-5">
              {outdated.map((c) => (
                <li key={c.widgetId ?? c.dashboardId} className="flex flex-wrap items-center gap-2">
                  <Link to="/d/$id" params={{ id: c.dashboardId }} className="font-medium hover:underline">{copyTitle(c)}</Link>
                  <span>v{c.version}</span>
                  {can.install && <Button size="sm" onClick={() => setUpdating(c)}>Update to v{l.version}</Button>}
                </li>
              ))}
            </ul>
          </div>
        ) : l.copies.length > 0 && (
          <p className="mt-2 text-xs text-muted">
            You have the latest version on{" "}
            {l.copies.map((c, n) => (
              <span key={c.widgetId ?? c.dashboardId}>
                {n > 0 && ", "}
                <Link to="/d/$id" params={{ id: c.dashboardId }} className="hover:text-foreground hover:underline">{c.dashboardTitle}</Link>
              </span>
            ))}
            .
          </p>
        )}
        {(staff || (l.mine && can.own)) && (
          <div className="mt-3 flex flex-wrap items-center gap-2 rounded-lg bg-surface-2 px-3 py-2 text-xs">
            {staff && (
              <>
                <span className="font-medium text-muted">Staff</span>
                <Button size="sm" disabled={moderate.isPending} onClick={() => moderate.mutate({ status: l.status === "listed" ? "unlisted" : "listed" })}>
                  <EyeOff className="size-3" /> {l.status === "listed" ? "Unlist" : "Relist"}
                </Button>
                <Button size="sm" disabled={moderate.isPending} onClick={() => moderate.mutate({ verified: !l.verified })}>
                  <BadgeCheck className="size-3" /> {l.verified ? "Remove verified" : "Mark verified"}
                </Button>
                <Button size="sm" disabled={moderate.isPending} onClick={() => moderate.mutate({ featured: !l.featured })}>
                  <Sparkles className="size-3" /> {l.featured ? "Unfeature" : "Feature"}
                </Button>
              </>
            )}
            {l.mine && can.own && (
              <>
                <span className="font-medium text-muted">{staff ? "· Yours" : "Your listing"}</span>
                {confirmUnpublish ? (
                  <>
                    <span>Remove it from the marketplace? Installed copies stay.</span>
                    <Button size="sm" variant="danger" disabled={unpublish.isPending} onClick={() => unpublish.mutate()}>Unpublish</Button>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmUnpublish(false)}>Cancel</Button>
                  </>
                ) : (
                  <Button size="sm" onClick={() => setConfirmUnpublish(true)}><Trash2 className="size-3" /> Unpublish</Button>
                )}
              </>
            )}
          </div>
        )}
        {error && <p className="mt-2 text-sm text-bad">{error.message}</p>}
      </header>

      <div className="mb-8 flex flex-wrap gap-x-10 gap-y-4 text-sm">
        <div className="min-w-0 max-w-2xl flex-1 space-y-3">
          {l.description ? <p className="whitespace-pre-line text-muted">{l.description}</p> : <p className="text-subtle">No description.</p>}
          {l.tags.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {l.tags.map((t) => <span key={t} className="rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] text-muted">#{t}</span>)}
            </div>
          )}
        </div>
        <div className="w-64">
          <h3 className="text-[11px] font-semibold uppercase tracking-wide text-subtle">Versions</h3>
          <ol className="mt-1.5 space-y-2">
            {l.versions.map((v) => (
              <li key={v.version}>
                <span className="font-medium">v{v.version}</span>{" "}
                <time dateTime={v.createdAt} className="text-xs text-subtle">
                  {new Date(v.createdAt).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                </time>
                {l.copies.some((c) => c.version === v.version) && <Badge className="ml-1.5">Installed</Badge>}
                {l.installedVersion !== undefined && v.version > l.installedVersion && <Badge tone="warn" className="ml-1.5">New</Badge>}
                {v.notes && <p className="text-xs text-muted">{v.notes}</p>}
              </li>
            ))}
          </ol>
        </div>
      </div>

      <section>
        <h2 className="mb-1 text-sm font-semibold">Preview with your data</h2>
        <p className="mb-3 text-xs text-muted">
          {selectionLabel(asSelection(l.snapshot.dashboard?.globalFilters))} · your locations. Nothing is added to your account until you install.
        </p>
        <Preview listing={l} />
      </section>

      <InstallDialog open={installOpen} onOpenChange={setInstallOpen} listing={l} />
      {updating && (
        <UpdateDialog
          key={updating.widgetId ?? updating.dashboardId} open onOpenChange={(o) => !o && setUpdating(undefined)} listingId={l.id} title={copyTitle(updating)}
          target={updating.widgetId ? { widgetId: updating.widgetId } : { dashboardId: updating.dashboardId }}
        />
      )}
    </div>
  );
}

/** The snapshot's widgets, run as the viewer (so only their locations), read-only. */
function Preview({ listing: l }: { listing: Detail }) {
  const time = useMemo(() => asSelection(l.snapshot.dashboard?.globalFilters), [l.snapshot]);
  const widgets = useMemo(
    () => Object.fromEntries(l.snapshot.widgets.map((w): [string, Widget] => [
      w.key, { id: w.key, version: l.version, ownerId: "", definition: w.definition, createdAt: l.createdAt, updatedAt: l.updatedAt },
    ])),
    [l],
  );
  const d = l.snapshot.dashboard;
  if (!d) {
    const w = Object.values(widgets)[0]!;
    return <div className="h-80 max-w-3xl"><WidgetCard widget={w} time={time} canEdit={false} onEdit={noop} onDuplicate={noop} onRemove={noop} /></div>;
  }
  return (
    <Grid
      className="-mx-2"
      layouts={d.layout as unknown as Layouts}
      breakpoints={BREAKPOINTS}
      cols={GRID_COLS}
      rowHeight={32}
      margin={[16, 16]}
      containerPadding={[8, 0]}
      isDraggable={false}
      isResizable={false}
    >
      {d.items.map((item) => {
        const w = widgets[item.widgetId];
        return (
          <div key={item.i}>
            {w && <WidgetCard widget={w} time={time} canEdit={false} onEdit={noop} onDuplicate={noop} onRemove={noop} />}
          </div>
        );
      })}
    </Grid>
  );
}
